import { Controller, Post, Get, Param, Body, Put, Delete, Patch, UseGuards, Req, Query, HttpException, HttpStatus } from '@nestjs/common';
import { Request } from 'express';
import { OrderService } from '../services/order.service';
import { CreateOrderDto, UpdatePaymentDto, ProcessPaymentDto, RefundPaymentDto } from '../dto';
import { JwtAuthGuard } from '@/common/guards/jwt-auth.guard';
import { ApiTags, ApiOperation, ApiParam, ApiBody, ApiResponse } from '@nestjs/swagger';
import { Roles } from '@/common/guards/roles.decorator';
import { UserRole } from '@/modules/user/schemas/user.schema';

@Controller('orders')
@ApiTags('orders')
export class OrderController {
  constructor(private readonly orderService: OrderService) {}

  @Post()
  @ApiOperation({ summary: 'Create a new order' })
  @ApiResponse({ status: 201, description: 'Order created successfully' })
  @ApiResponse({ status: 400, description: 'Bad request' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async create(@Body() createOrderDto: CreateOrderDto, @Req() request: Request) {
    try {
      const result = await this.orderService.create(createOrderDto, request);
      return result;
    } catch (error) {
      // Return a proper error response instead of letting it become a 500
      throw new HttpException({
        statusCode: 400,
        message: error.message || 'Order creation failed',
        error: 'Bad Request'
      }, HttpStatus.BAD_REQUEST);
    }
  }

  @Get()
  @UseGuards(JwtAuthGuard)
  @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Get all orders' })
  @ApiResponse({ status: 200, description: 'Orders retrieved successfully' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async findAll(@Query() query: any) {
    return this.orderService.findAll(query);
  }

  @Get('number/:orderNumber')
  @ApiOperation({ summary: 'Get order by order number' })
  @ApiParam({ name: 'orderNumber', description: 'Order Number (e.g., ORD-12345)' })
  @ApiResponse({ status: 200, description: 'Order retrieved successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  async findByOrderNumber(@Param('orderNumber') orderNumber: string) {
    try {
      return await this.orderService.findByOrderNumber(orderNumber);
    } catch (error) {
      throw new HttpException({
        statusCode: 404,
        message: error.message || 'Order not found',
        error: 'Not Found'
      }, HttpStatus.NOT_FOUND);
    }
  }

  @Get(':id')
  // @Roles(UserRole.ADMIN, UserRole.CUSTOMER)
  @ApiOperation({ summary: 'Get order by ID' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiResponse({ status: 200, description: 'Order retrieved successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async findOne(@Param('id') id: string) {
    return this.orderService.findOne(id);
  }

  @Put(':id')
  // @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Update order' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiResponse({ status: 200, description: 'Order updated successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 400, description: 'Bad request' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async update(@Param('id') id: string, @Body() updateOrderDto: Partial<CreateOrderDto>) {
    try {
      const result = await this.orderService.update(id, updateOrderDto);
      
      
      return result;
    } catch (error) {
      throw new HttpException({
        statusCode: 400,
        message: error.message || 'Order update failed',
        error: 'Bad Request'
      }, HttpStatus.BAD_REQUEST);
    }
  }

  @Delete(':id')
  // @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Delete order' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiResponse({ status: 200, description: 'Order deleted successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async remove(@Param('id') id: string) {
    return this.orderService.remove(id);
  }

  @Post(':id/process-payment')
  // @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Process payment for order' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiBody({ type: ProcessPaymentDto })
  @ApiResponse({ status: 200, description: 'Payment processed successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 400, description: 'Payment processing failed' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async processPayment(@Param('id') id: string, @Body() processPaymentDto: ProcessPaymentDto) {
    try {
      // TODO: Implement processPayment method in OrderService
      return { message: 'Payment processing not implemented yet', orderId: id, paymentData: processPaymentDto };
    } catch (error) {
      throw new HttpException({
        statusCode: 400,
        message: error.message || 'Payment processing failed',
        error: 'Bad Request'
      }, HttpStatus.BAD_REQUEST);
    }
  }

  @Patch(':id/payment')
  // @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Update payment information for order' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiBody({ type: UpdatePaymentDto })
  @ApiResponse({ status: 200, description: 'Payment updated successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 400, description: 'Payment update failed' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async updatePayment(@Param('id') id: string, @Body() updatePaymentDto: UpdatePaymentDto) {
    try {
      // TODO: Implement updatePayment method in OrderService
      return { message: 'Payment update not implemented yet', orderId: id, paymentUpdate: updatePaymentDto };
    } catch (error) {
      throw new HttpException({
        statusCode: 400,
        message: error.message || 'Payment update failed',
        error: 'Bad Request'
      }, HttpStatus.BAD_REQUEST);
    }
  }

  @Post(':id/refund')
  // @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Process refund for order' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiBody({ type: RefundPaymentDto })
  @ApiResponse({ status: 200, description: 'Refund processed successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 400, description: 'Refund processing failed' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async processRefund(@Param('id') id: string, @Body() refundDto: RefundPaymentDto) {
    try {
      // TODO: Implement processRefund method in OrderService
      return { message: 'Refund processing not implemented yet', orderId: id, refundData: refundDto };
    } catch (error) {
      throw new HttpException({
        statusCode: 400,
        message: error.message || 'Refund processing failed',
        error: 'Bad Request'
      }, HttpStatus.BAD_REQUEST);
    }
  }

  // Legacy method for backward compatibility
  @Post(':id/process-cash-payment')
  // @Roles(UserRole.ADMIN)
  @ApiOperation({ summary: 'Process cash payment for order (Legacy)' })
  @ApiParam({ name: 'id', description: 'Order ID' })
  @ApiBody({
    schema: {
      type: 'object',
      properties: {
        amountPaid: {
          type: 'number',
          description: 'Amount paid by customer',
          example: 1000
        }
      }
    }
  })
  @ApiResponse({ status: 200, description: 'Payment processed successfully' })
  @ApiResponse({ status: 404, description: 'Order not found' })
  @ApiResponse({ status: 400, description: 'Insufficient payment amount' })
  @ApiResponse({ status: 401, description: 'Unauthorized' })
  async processCashPayment(@Param('id') id: string, @Body('amountPaid') amountPaid: number) {
    return this.orderService.processCashPayment(id, amountPaid);
  }
}
